import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  User, UserType, Claim, ClaimType, transformClaim, Item, Comment,
  transformStore, Store, Channel, isAlertClaimType, AlertClaim, AlertClaimItem, AlertType
} from '../types';

import Toast from 'components/Toast';
import { BadRequestHandler } from 'components/Modal';
import download from 'downloadjs';
import { getAlertClaims, getAlertClaim, updateAlertClaim, deleteAlertClaim } from './AlertClaimService';

import { apiconfig } from '../apiconfig';
import { updateAlertClaimItem } from 'services/AlertClaimItemService';

let baseURL = apiconfig.base_url;

export { baseURL };

const handler = BadRequestHandler.handler;

const instance = axios.create({
  baseURL: baseURL
});

let token: string | null = localStorage.getItem('token');

export const setToken = (newToken: string | null) => {
  token = newToken;
  token ? localStorage.setItem('token', token) : localStorage.removeItem('token');
};

function addAuthentication(config: AxiosRequestConfig) {
  if (token && (config.url!!.indexOf(config.baseURL!!) === 0 || config.url!![0] === '/')) { // request to server!
    config.headers.common.Authorization = 'Bearer ' + token;
  }
  return config;
}

instance.interceptors.request.use(addAuthentication);

function handleErrors(error: any) {
  if (error.response && (error.response.status === 400 || error.response.status === 401))
    return Promise.reject(error);
  if (error.response && error.response.data && error.response.data.message)
    Toast.throw({ message: 'Request failed: ' + error.response.data.message });
  else if (error.message)
    Toast.throw({ message: 'Request failed: ' + error.message });
  else
    Toast.throw({ message: 'Request failed' });
  /* tslint:disable-next-line */
  return { then: function () { } };
}

instance.interceptors.response.use(undefined, handleErrors);

export { instance, addAuthentication, handleErrors };

export class UserService {
  private static userId: number | null = null;
  private static userType: UserType | null = null;
  static getType = () => {
    return UserService.userType;
  }
  static fetch = (id: number = UserService.userId!!): Promise<User> => {
    return new Promise((resolve, reject) => {
      instance.get('/users/' + id).then(({ data }) => {
        resolve(data);
      }, handler(reject));
    });
  }
  static login = (credentials: object): Promise<User> => {
    return new Promise((resolve, reject) => {
      instance.post('/login', credentials).then(({ data }) => {
        setToken(data.token);
        UserService.userId = data.id;
        UserService.userType = data.type;
        UserService.fetch().then((userData) => {
          if (userData.type === UserType.Grocery || userData.type === UserType.GrocerReporter) {
            UserService.logout().then(() => {
              handler(reject)({
                response: { data: { message: 'This portal is only for LCBO Internal Use' } }
              });
            });
          } else {
            resolve(userData);
          }
        }, handler(reject));
      }, (error) => {
        handler(reject)(error.response.status === 401 ? {
          response: { data: { message: 'Invalid login - check your credentials' } }
        } : error);
      });
    });
  }
  static checkAuth = () => {
    return UserService.userId !== null && UserService.userType !== null;
  }
  static tryAuth = (): Promise<boolean> => {
    if (UserService.userId !== null) return Promise.resolve(true);
    if (token === null) return Promise.resolve(false);
    return new Promise((resolve, reject) => {
      instance.get('/authorized').then(({ data }) => {
        UserService.userId = data.id;
        UserService.userType = data.type;
        UserService.fetch().then(() => resolve(true), reject);
      }, () => {
        resolve(false);
      });
    });
  }
  static logout = () => {
    return new Promise((resolve, reject) => {
      instance.post('/logout').then(success => {
        setToken(null);
        UserService.userId = null;
        UserService.userType = null;
        resolve(success);
      }, handler(reject));
    });
  }  
  static update = (userData: User, id: number = UserService.userId!!): Promise<User> => {
    return new Promise<User>((resolve, reject) => {
      instance.patch(`/users/${id}`, userData).then(({ data }) => {
        resolve(data);
      }, handler(reject));
    });
  }
  static getAllUsers = () => {
    return new Promise<User[]>((resolve, reject) => {
      instance.get('/users').then(({ data }) => resolve(data), handler(reject));
    });
  }
  static getAllGrocerUsers = () => {
    return new Promise<User[]>((resolve, reject) => {
      instance.get('/grocer/users').then(({ data }) => resolve(data), handler(reject));
    });
  }

  static deleteUser = (id: number) => {
    return new Promise((resolve, reject) => {
      instance.delete('/users/' + id).then(resolve, handler(reject));
    });
  }
  static deactivateUser = (id: number) => {
    return new Promise((resolve, reject) => {
      instance.patch('/users/deactivate/' + id).then(resolve, handler(reject));
    });
  }
  static createUser = (user: User) => {
    return new Promise((resolve, reject) => {
      instance.post('/users', user).then(resolve, handler(reject));
    });
  }
}

export class StoreService {
  static fetchAll = () => {
    return new Promise<Store[]>((resolve, reject) => {
      instance.get('/stores').then(({ data }) => {
        resolve(data.map(transformStore));
      }, handler(reject));
    });
  }
  static fetch = (id: number) => {
    return new Promise<Store>((resolve, reject) => {
      instance.get('/stores/' + id).then(({ data }) =>
        resolve(transformStore(data)), handler(reject));
    });
  }
  static fetchInChannel = (id: number) => {
    return new Promise<Store[]>((resolve, reject) => {
      instance.get('/storesInChannels/' + id).then(({ data }) =>
        resolve(data), handler(reject));
    });
  }

  static patch(store: Store) {
    return new Promise<Store>((resolve, reject) => {
      instance.patch('/stores/' + store.id, store)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }
  static create(store: Store) {
    delete store.id;
    return new Promise<Store>((resolve, reject) => {
      instance.post('/stores', store)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }
  static delete(id: number) {
    return new Promise<Store>((resolve, reject) => {
      instance.delete('/stores/' + id)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }
}

export class ClaimService {
  static fetch = (type: ClaimType, id: number) => {
    if (isAlertClaimType(type)) {
      return getAlertClaim(id.toString()).then((claim) => claim.toGenericClaim());
    }
    return new Promise<Claim<Item>>((resolve, reject) => {
      instance.get('/' + type + 's/' + id).then(({ data }) =>
        resolve(transformClaim(type, data)), handler(reject));
    });
  }
  static patch(claim: Partial<Claim<Item>>) {
    if (claim.type && claim.id && isAlertClaimType(claim.type)) {
      return updateAlertClaim(claim.id.toString(), AlertClaim.genericToPatch(claim))
        .then((freshClaim) => freshClaim.toGenericClaim());
    }
    return new Promise<Claim<Item>>((resolve, reject) => {
      instance.patch('/' + claim.type + 's/' + claim.id, claim)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }

  static patchItem(claim: Claim<Item>, item: Partial<Item>) {
    if (isAlertClaimType(claim.type)) {
      if (item.id !== undefined) {
        return updateAlertClaimItem(item.id.toString(), AlertClaimItem.genericToPatch(item))
          .then((freshItem) => freshItem.toGenericItem());
      }
    }
    return new Promise<Item>((resolve, reject) => {
      instance.patch('/' + claim.type + 's/' + claim.id + '/items/' + item.id, item)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }
  static comment(claim: Claim<Item>, message: string) {
    let claimType = isAlertClaimType(claim.type) ? 'alert_claim' : claim.type;
    // if (isAlertClaimType(claim.type)) {
    //   // TODO RECALLS MODULE: not implemented. This currently requires backend changes in order to be completed.
    //   throw new Error('Comments on alert claims is not yet implemented...');
    // }
    return new Promise<Comment>((resolve, reject) => {
      instance.post('/' + claimType + 's/' + claim.id + '/comments', { message })
        .then(({ data }) => resolve(data), handler(reject));
    });
  }

  static delete(type: ClaimType, id: number) {
    if (isAlertClaimType(type)) {
      return deleteAlertClaim(id.toString());
    }
    return new Promise((resolve, reject) => {
      instance.delete('/' + type + 's/' + id)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }

  static assign(type: ClaimType, id: number, assignedUserId: number) {
    if (isAlertClaimType(type)) {
      return updateAlertClaim(id.toString(), { assignedToId: assignedUserId.toString() })
        .then((claim) => claim.toGenericClaim());
    }
    return new Promise((resolve, reject) => {
      instance.post(`/${type}s/${id}/tag`, { assignedUserId })
        .then(({ data }) => resolve(data), handler(reject));
    });
  }

  static unassign(type: ClaimType, id: number) {
    if (isAlertClaimType(type)) {
      return updateAlertClaim(id.toString(), { assignedToId: null })
        .then((claim) => claim.toGenericClaim());
    }
    return new Promise((resolve, reject) => {
      instance.delete(`/${type}s/${id}/tag`)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }

  static fetchAll = (queryString: string, user: User | null, ignoreDrafts: boolean = true) => {
    const alertClaimType = user ? UserType.alertType(user.type) : null;
    const shouldFetchRefunds = !user || UserType.shouldFetchRefundClaims(user);
    const shouldFetchAdjustments = !user || UserType.shouldFetchAdjustmentClaims(user);
    return new Promise<Claim<Item>[]>((resolve, reject) => {
      Promise.all([
        shouldFetchRefunds ? instance.get('/refundsItemsAll?' + queryString) : Promise.resolve(null),
        shouldFetchAdjustments ? instance.get('/adjustmentsItemsAll?' + queryString) : Promise.resolve(null),
        alertClaimType === null ? Promise.resolve([]) : getAlertClaims(alertClaimType),
      ])
        .then(results => {
          let claims: Claim<Item>[] = [];
          /* This if statement should allow TypeScript to perform type interpolation, 
          and know that results[0] is not null.
          However, it doesn't seem to work here. This has been tested to work in TypeScript 4.2.3,
          so the cast can likely be removed when upgrading TypeScript. */
          if (results[0]) {
            for (const refund of (results[0] as AxiosResponse<any>).data) {
              refund.type = 'refund';
              if (!ignoreDrafts || refund.status !== 'draft') claims.push(transformClaim('refund', refund));
            }
          }
          if (results[1]) {
            for (const adjustment of (results[1] as AxiosResponse<any>).data) {
              adjustment.type = 'adjustment';
              if (!ignoreDrafts || adjustment.status !== 'draft') claims.push(transformClaim('adjustment', adjustment));
            }
          }
          if (results[2]) {
            for (const claim of results[2]) {
              const isQAAdmin = user !== null && user.type === UserType.QAAdmin;
              const genericClaim = claim.toGenericClaim(isQAAdmin);
              if (genericClaim.status !== 'draft' && !(queryString.includes('hideCompleted') && genericClaim.status === 'completed')) {
                claims.push(genericClaim);
              }
            }
          }
          claims.sort((a, b) => b.date.valueOf() - a.date.valueOf());
          resolve(claims);
        },
        (...stuff) => {
          return handler(reject)(stuff);
        });
    });
  }

  static fetchAllItems = (queryString: string, ignoreDrafts: boolean = true) => {
    return new Promise<Claim<Item>[]>((resolve, reject) => {
      Promise.all([
        instance.get('/refundsItems?' + queryString),
        instance.get('/adjustmentsItems?' + queryString),
        getAlertClaims([AlertType.PriceRecall, AlertType.QAAlert]),
      ]).then((results) => {
        let claims: Claim<Item>[] = [];
        for (const refund of results[0].data) {
          refund.type = 'refund';
          if (!ignoreDrafts || refund.status !== 'draft')
            claims.push(transformClaim('refund', refund));
        }
        for (const adjustment of results[1].data) {
          adjustment.type = 'adjustment';
          if (!ignoreDrafts || adjustment.status !== 'draft')
            claims.push(transformClaim('adjustment', adjustment));
        }
        for (const claim of results[2]) {
          const genericClaim = claim.toGenericClaim();
          if (
            genericClaim.status !== 'draft' &&
            !(
              queryString.includes('hideCompleted') &&
              genericClaim.status === 'completed'
            )
          ) {
            claims.push(genericClaim);
          }
        }
        claims.sort((a, b) => b.createDate.valueOf() - a.createDate.valueOf());
        resolve(claims);
      }, handler(reject));
    });
  }

  static fetchAllItemsAcrossAllUser = (queryString: string, ignoreDrafts: boolean = true) => {
    return new Promise<Claim<Item>[]>((resolve, reject) => {
      Promise.all([instance.get('/refundsItemsAll?' + queryString),
      instance.get('/adjustmentsItemsAll?' + queryString),
      getAlertClaims([AlertType.QAAlert, AlertType.PriceRecall])]).then(results => {
        let claims: Claim<Item>[] = [];
        for (const refund of results[0].data) {
          refund.type = 'refund';
          if (!ignoreDrafts || refund.status !== 'draft') claims.push(transformClaim('refund', refund));
        }
        for (const adjustment of results[1].data) {
          adjustment.type = 'adjustment';
          if (!ignoreDrafts || adjustment.status !== 'draft') claims.push(transformClaim('adjustment', adjustment));
        }
        for (const claim of results[2]) {
          const genericClaim = claim.toGenericClaim();
          if (genericClaim.status !== 'draft') {
            claims.push(claim.toGenericClaim());
          }
        }
        claims.sort((a, b) => b.createDate.valueOf() - a.createDate.valueOf());
        resolve(claims);
      }, handler(reject));
    });
  }

  static fetchRatios = () => {
    return new Promise<object>((resolve, reject) => {
      instance.post('/claims/items/ratios').then(({ data }) =>
        resolve(data), handler(reject));
    });
  }

  static downloadPDF = (
    user: User | null,
    channelFilter: string,
    channelFilterName: string,
    hideCompleted: boolean,
    hideReturnInvoice: boolean,
    filterIds?: ReadonlyArray<Readonly<Claim<Item>['id']>>,
    claimType?: ClaimType) => {
    var exportFileName = ClaimService.exportFileName() +
      (user!!.type === UserType.LCBOReporter ? '_' + channelFilterName : '') + '.pdf';
    let params: {hideCompleted: boolean, hideReturnInvoice: boolean, filterIds: ReadonlyArray<Readonly<Claim<Item>['id']>> | undefined, claimType?: string } = {
      hideCompleted,
      hideReturnInvoice,
      filterIds
    };
    if (claimType) {
      params.claimType = JSON.stringify(claimType);
    }
    instance
      .get(baseURL + 'pdf/' + channelFilter + '/', {
        responseType: 'blob',
        params
      })
      .then(response => {
        download(response.data, exportFileName);
      })
      .catch(error => console.log(error));
  }

  static exportFileName() {
    let today = new Date();
    let year = today.getFullYear();
    let day = this.addLeadingZero(today.getDate());
    let month = this.addLeadingZero(today.getMonth() + 1);
    let hour = this.addLeadingZero(today.getHours());
    let minute = this.addLeadingZero(today.getMinutes());
    let seconds = this.addLeadingZero(today.getSeconds());
    let dateName = `${year}-${month}-${day}__${hour}.${minute}.${seconds}EST`;
    let exportFileName = `LCO_Claims_Export__${dateName}`;
    return exportFileName;
  }

  static addLeadingZero(time: Number) {
    let str = time.toString();
    if (str.length < 2) return '0' + str;
    return str;
  }
}

export class ChannelService {
  static fetch = (id: number) => {
    return new Promise<Channel>((resolve, reject) => {
      instance.get('/channels/' + id).then(({ data }) => {
        resolve(data);
      }, handler(reject));
    });
  }
  static fetchAll = () => {
    return new Promise<Channel[]>((resolve, reject) => {
      instance.get('/channels').then(({ data }) => {
        resolve(data);
      }, handler(reject));
    });
  }
  static create = (channel: Channel) => {
    delete channel.id;
    return new Promise<Channel>((resolve, reject) => {
      instance.post('/channels', channel).then(({ data }) => resolve(data), handler(reject));
    });
  }
  static patch = (channel: Channel) => {
    return new Promise<Channel>((resolve, reject) => {
      instance.patch('/channels/' + channel.id, channel).then(({ data }) => resolve(data), handler(reject));
    });
  }
  static delete(id: number) {
    return new Promise((resolve, reject) => {
      instance.delete('/channels/' + id)
        .then(({ data }) => resolve(data), handler(reject));
    });
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/services/ApiService.ts